import { type CfAccountInfo, type CfUnregisteredAccount } from '@chainflip/rpc/types';
import { isNotNullish } from '@chainflip/utils/guard';
import { useQuery } from '@tanstack/react-query';
import { accountInfoRpcKey } from '@/shared/queryKeys';
import { isChainflipSs58Address } from '@/shared/utils/helpers';
import makeRpcRequest from '@/shared/utils/rpc';

// `account_id` became a required field on the account-info parsers in @chainflip/rpc 2.3
const unregistered = (accountId: `cF${string}`): CfUnregisteredAccount => ({
  role: 'unregistered',
  account_id: accountId,
  flip_balance: 0n,
  bond: 0n,
  estimated_redeemable_balance: 0n,
  asset_balances: {
    Arbitrum: { ETH: 0n, USDC: 0n, USDT: 0n },
    Assethub: { DOT: 0n, USDC: 0n, USDT: 0n },
    Bitcoin: { BTC: 0n },
    Ethereum: { ETH: 0n, USDC: 0n, USDT: 0n, FLIP: 0n, WBTC: 0n, CBBTC: 0n },
    Solana: { SOL: 0n, USDC: 0n, USDT: 0n },
    Tron: { USDT: 0n, TRX: 0n },
    Bsc: { BNB: 0n, USDT: 0n },
  },
});

export type AccountInfoWithId = CfAccountInfo & { id: `cF${string}` };

export const fetchAccountInfo = (accountId: `cF${string}`): Promise<AccountInfoWithId> =>
  makeRpcRequest('cf_account_info', accountId!)
    .catch((err) => {
      if (err instanceof Error && err.message.includes('Unknown Account')) {
        return unregistered(accountId);
      }

      throw err;
    })
    .then((data) => ({ ...data, id: accountId }));

export default function useAccountInfo<T = AccountInfoWithId>(
  accountId: string | null | undefined,
  {
    refetchInterval,
    select,
  }: { refetchInterval?: number; select?: (data: AccountInfoWithId) => T } = {},
) {
  return useQuery({
    queryKey: accountInfoRpcKey(accountId as `cF${string}` | null | undefined),
    queryFn: () => fetchAccountInfo(accountId as `cF${string}`),
    enabled: isNotNullish(accountId) && isChainflipSs58Address(accountId),
    refetchInterval,
    select,
  });
}
